import React, { useState, useEffect, useCallback } from 'react';
import { FaMoon, FaTimes, FaBell, FaBellSlash } from 'react-icons/fa';
import { useToast } from '../../contexts/ToastContext';
import { usePlayerStore } from '../../stores/playerStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { sleepTimerService } from '../../services/sleepTimerService';
import { createPortal } from 'react-dom';

interface SleepTimerProps {
  onClose: () => void;
}

// 5 and 10 added so the timer can be confirmed working without waiting a quarter
// of an hour — the shortest option used to be 15 minutes.
const PRESET_TIMES = [5, 10, 15, 30, 45, 60, 90, 120];

const SleepTimer: React.FC<SleepTimerProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const { mode } = useTheme();
  const isFairyTheme = mode === 'fairy';
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
  const [customMinutes, setCustomMinutes] = useState('');
  const { sleepTimer } = usePlayerStore();
  const { pushNotificationsEnabled, setPushNotificationsEnabled } = useSettingsStore();
  const { showToast } = useToast();

  // Initialize remaining time from service
  useEffect(() => {
    const remaining = sleepTimerService.getDisplayTime();
    if (remaining !== null) {
      setRemainingSeconds(remaining);
    } else if (sleepTimer) {
      setRemainingSeconds(sleepTimer * 60);
    } else {
      setRemainingSeconds(null);
    }
  }, [sleepTimer]);

  const handleSetTimer = useCallback((minutes: number) => {
    sleepTimerService.startTimer(minutes);
    setRemainingSeconds(minutes * 60);
    showToast(t('sleep_timer.set', { minutes }), 'success');
    // Stay open briefly so the countdown is visible — closing instantly made it
    // look like setting a timer had done nothing at all.
    setTimeout(onClose, 1200);
  }, [showToast, t, onClose]);

  const handleCustomTimeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const minutes = parseInt(customMinutes);
    if (!isNaN(minutes) && minutes > 0) {
      handleSetTimer(minutes);
    }
  };

  const handleClearTimer = () => {
    sleepTimerService.stopTimer();
    setRemainingSeconds(null);
    showToast(t('sleep_timer.cancelled'), 'info');
    onClose();
  };

  // Update remaining time display
  useEffect(() => {
    if (remainingSeconds === null) return;

    const interval = setInterval(() => {
      const remaining = sleepTimerService.getDisplayTime();
      if (remaining === null) {
        setRemainingSeconds(null);
        clearInterval(interval);
      } else {
        setRemainingSeconds(remaining);
        // Hold the display at 0:00 rather than counting into negatives; the
        // service handles the actual stop.
        if (remaining <= 0) clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [remainingSeconds]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSecs = seconds % 60;
    return `${minutes}:${remainingSecs.toString().padStart(2, '0')}`;
  };

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={`${isFairyTheme
          ? 'bg-[#2a1f3f] border border-purple-500/20'
          : mode === 'neon'
            ? 'bg-black/95 border border-[#00FF00]/20'
            : 'bg-gray-800'} 
        rounded-xl max-w-md w-full mx-4 p-6 relative animate-fade-in z-[10000]`}
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className={`absolute top-4 right-4 ${isFairyTheme
            ? 'text-purple-200 hover:text-purple-100'
            : mode === 'neon'
              ? 'text-[#00FF00]/80 hover:text-[#00FF00]'
              : 'text-gray-400 hover:text-white'} transition-colors`}
        >
          <FaTimes size={20} />
        </button>

        <div className="flex items-center space-x-2 mb-6">
          <FaMoon size={24} className={isFairyTheme
            ? 'text-purple-400'
            : mode === 'neon'
              ? 'text-[#00FF00]'
              : 'text-primary'} />
          <h2 className={`text-xl font-semibold ${isFairyTheme
            ? 'text-purple-200'
            : mode === 'neon'
              ? 'text-[#00FF00]'
              : 'text-white'}`}>
            {t('sleep_timer.title')}
          </h2>
        </div>

        {remainingSeconds !== null && (
          <div className="mb-6 text-center">
            <div className={`text-3xl font-bold mb-2 ${isFairyTheme
              ? 'text-purple-300'
              : mode === 'neon'
                ? 'text-[#00FF00]'
                : 'text-primary'}`}>
              {formatTime(remainingSeconds)}
            </div>
            <button
              onClick={handleClearTimer}
              className={`text-sm ${isFairyTheme
                ? 'text-purple-400 hover:text-purple-300'
                : mode === 'neon'
                  ? 'text-[#00FF00]/80 hover:text-[#00FF00]'
                  : 'text-primary hover:text-primary/80'} transition-colors`}
            >
              {t('sleep_timer.cancel')}
            </button>
          </div>
        )}

        <div className="grid grid-cols-3 gap-3 mb-6">
          {PRESET_TIMES.map((minutes) => (
            <button
              key={minutes}
              onClick={() => handleSetTimer(minutes)}
              className={`p-3 rounded-lg text-center transition-colors ${isFairyTheme
                ? 'bg-purple-800/30 hover:bg-purple-700/30 text-purple-300'
                : mode === 'neon'
                  ? 'bg-[#00FF00]/10 hover:bg-[#00FF00]/20 text-[#00FF00]'
                  : 'bg-gray-700 hover:bg-gray-600 text-white'
                }`}
            >
              {t('sleep_timer.preset_time', { minutes })}
            </button>
          ))}
        </div>

        <form onSubmit={handleCustomTimeSubmit} className="mb-6">
          <div className="flex space-x-2">
            <input
              type="number"
              value={customMinutes}
              onChange={(e) => setCustomMinutes(e.target.value)}
              placeholder={t('sleep_timer.custom_minutes')}
              className={`flex-1 px-4 py-2 rounded-lg ${isFairyTheme
                ? 'bg-purple-800/30 text-purple-300 placeholder-purple-400'
                : mode === 'neon'
                  ? 'bg-[#00FF00]/10 text-[#00FF00] placeholder-[#00FF00]/50'
                  : 'bg-gray-700 text-white placeholder-gray-400'
                }`}
              min="1"
            />
            <button
              type="submit"
              className={`px-4 py-2 rounded-lg transition-colors ${isFairyTheme
                ? 'bg-purple-500 hover:bg-purple-600 text-white'
                : mode === 'neon'
                  ? 'bg-[#00FF00] hover:bg-[#00FF00]/90 text-black'
                  : 'bg-primary hover:bg-primary/90 text-black'
                }`}
            >
              {t('sleep_timer.set_button')}
            </button>
          </div>
        </form>

        <div className="flex items-center justify-between text-sm text-gray-400">
          <div className="flex items-center space-x-2">
            {pushNotificationsEnabled ? <FaBell /> : <FaBellSlash />}
            <span className={mode === 'neon' ? 'text-[#00FF00]/80' : ''}>
              {t('settings.notifications.push.title')}
            </span>
          </div>
          <button
            onClick={() => setPushNotificationsEnabled(!pushNotificationsEnabled)}
            className={`px-2 py-1 rounded transition-colors ${pushNotificationsEnabled
              ? isFairyTheme
                ? 'bg-purple-500 text-white'
                : mode === 'neon'
                  ? 'bg-[#00FF00] text-black'
                  : 'bg-primary text-black'
              : mode === 'neon'
                ? 'bg-[#00FF00]/10 text-[#00FF00]'
                : 'bg-gray-700 text-white'
              }`}
          >
            {pushNotificationsEnabled ? t('enabled') : t('disabled')}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default SleepTimer; 